// Sprint A.9 — persistent SDLC phase stepper. Mounted once in ProjectLayout
// above <Outlet />; survives intra-project navigation without remounting.
//
// Conventions (mirrors HelpPopover.tsx header):
// - Inline styles + CSS variables (no Radix, no new top-level deps)
// - Mandatory `id` prop with default for telemetry anchoring + a11y
// - Native <button> elements for keyboard discovery (Tab/Enter/Space)
// - ARIA: nav landmark + aria-current="step" on the current phase

import { useNavigate } from 'react-router-dom';
import { PhaseKey, phaseConfig, PHASE_DISPLAY_NUMBER } from './PhaseBadge';
import { useProjectPhases, type PhaseTimelineStatus } from '../hooks/useProjectPhases';

// Q1=(b) — render the core 8 SDLC steps. Excludes phase_0_bootstrap
// (invisible pre-work) and phase_8_retrospective (post-deployment). Phase
// 2.5 (UX Design) counts as one step; its display number is '2.5' via
// PHASE_DISPLAY_NUMBER (NEVER render by array index — PhaseBadge.tsx:26-36
// warns against this).
// Exported so the dashboard's ?phase= consumer can validate against the
// same set the stepper emits (R1 L2 fix — narrower than ALL_PHASES,
// rejects manually crafted URLs pinning bootstrap/retrospective).
export const VISIBLE_PHASES: PhaseKey[] = [
  'phase_1_requirements',
  'phase_2_architecture',
  'phase_2_5_ux_design',
  'phase_3_sprint_planning',
  'phase_4_development',
  'phase_5_code_review',
  'phase_6_testing',
  'phase_7_deployment',
];

// Q5 — status dot semantics. Verified against
// apps/api/src/services/phase-signoff.service.ts:55, 149: the API writes
// `awaiting_signoff` when a phase exit-gate requests client sign-off, and
// `changes_requested` when the client rejects. `blocked` is a defensive
// branch in the API response mapper (phase-transitions.ts:241) but no
// service writes it as of this commit — included here for forward-compat.
// `active` and `completed` are already conveyed by the circle visual, so
// no dot for them. `pending` is the default upcoming state — no dot.
const STATUS_DOT: Partial<Record<PhaseTimelineStatus, { color: string; tooltip: string }>> = {
  awaiting_signoff:  { color: 'var(--warning, #d97706)', tooltip: 'Awaiting client sign-off' },
  changes_requested: { color: 'var(--error, #dc2626)',   tooltip: 'Changes requested' },
  blocked:           { color: 'var(--error, #dc2626)',   tooltip: 'Blocked' },
};

interface ProjectPhaseStepperProps {
  projectId: string;
  /** Mandatory id for ARIA anchoring + future telemetry; defaulted because
   * the component is currently mounted once from ProjectLayout. */
  id?: string;
}

export default function ProjectPhaseStepper({
  projectId,
  id = 'project-phase-stepper',
}: ProjectPhaseStepperProps) {
  const { currentPhase, phases, loading, error } = useProjectPhases(projectId);
  const navigate = useNavigate();

  // #2331 — a caller with no tenant_members row (e.g. a project_members-only
  // account newly routed into /projects/:id/open-questions via
  // ProjectMemberDashboard.tsx) 403s the requireTenantMember-gated
  // phase-states endpoint this hook reads. Render nothing rather than the
  // loading skeleton below forever: the skeleton's `loading ||
  // !currentPhase` guard never distinguished "still fetching" from
  // "fetch failed", so an errored fetch left `currentPhase` permanently
  // null and this component stuck on its aria-busy skeleton indefinitely.
  // The rest of the page (e.g. OpenQuestionsPage itself) is unaffected —
  // this component's own data is independent of the page content below it.
  if (error) return null;

  // Skeleton occupies the same vertical footprint so layout doesn't shift
  // when data resolves.
  if (loading || !currentPhase) {
    return (
      <div
        id={id}
        aria-busy="true"
        aria-label="SDLC phase progress (loading)"
        style={{
          height: 56,
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0,
          // Match the loaded-state sticky positioning so the skeleton
          // doesn't cause a layout shift when data resolves.
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      />
    );
  }

  const currentIndex = VISIBLE_PHASES.indexOf(currentPhase);

  return (
    <nav
      id={id}
      aria-label="SDLC phase progress"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '10px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        overflowX: 'auto',
        flexShrink: 0,
        // Sticky positioning keeps the stepper visible during body scroll.
        // Mount persistence (Q2=a payoff) ensures the component doesn't
        // remount on intra-project nav; sticky ensures it doesn't scroll
        // out of view. The opaque background covers content scrolling
        // underneath. zIndex above the dashboard's own sticky breadcrumb
        // (z-10) would collide; equal z-index leaves rendering order to
        // DOM order — stepper is above breadcrumb in the tree, so it
        // wins. Scroll targets reached via scrollIntoView need
        // scroll-margin-top compensation matching this strip's rendered
        // height (~50px) — see ProjectDashboardPage's
        // #phase-progress-card anchor.
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      {VISIBLE_PHASES.map((phase, index) => {
        // Completed = strictly before the current index. `currentIndex === -1`
        // means the project's currentPhase isn't in VISIBLE_PHASES (e.g.
        // phase_0_bootstrap or phase_8_retrospective leaked through); no
        // step gets the completed treatment in that case.
        const isCompleted = currentIndex !== -1 && index < currentIndex;
        const isCurrent = phase === currentPhase;
        const phaseState = phases.find(p => p.phase === phase);
        const dot = phaseState?.status ? STATUS_DOT[phaseState.status] : undefined;
        const label = phaseConfig[phase].label;
        const num = PHASE_DISPLAY_NUMBER[phase];

        const ariaLabel = `Phase ${num} ${label}` +
          (isCurrent ? ' (current)' : isCompleted ? ' (completed)' : '') +
          (dot ? ` — ${dot.tooltip}` : '');

        return (
          <button
            key={phase}
            type="button"
            onClick={() => navigate(`/projects/${projectId}?phase=${phase}`)}
            aria-current={isCurrent ? 'step' : undefined}
            aria-label={ariaLabel}
            data-phase={phase}
            data-testid={`stepper-phase-${phase}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: 'var(--radius-md, 6px)',
              position: 'relative',
              flexShrink: 0,
              fontFamily: 'var(--font-body)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                fontSize: 11,
                fontWeight: 700,
                background: isCompleted ? 'var(--success, #10b981)'
                  : isCurrent ? 'var(--primary, #7c3aed)'
                  : 'var(--border, #e5e7eb)',
                color: (isCompleted || isCurrent) ? '#fff' : 'var(--text-secondary)',
                boxShadow: isCurrent ? '0 0 0 4px rgba(124,58,237,0.15)' : undefined,
              }}
            >
              {isCompleted ? '✓' : num}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: isCurrent ? 600 : 500,
                color: isCurrent ? 'var(--primary, #7c3aed)'
                  : isCompleted ? 'var(--text)'
                  : 'var(--text-secondary)',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
            {dot && (
              <span
                aria-hidden="true"
                title={dot.tooltip}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: dot.color,
                  marginLeft: 2,
                  flexShrink: 0,
                }}
              />
            )}
            {index < VISIBLE_PHASES.length - 1 && (
              <span
                aria-hidden="true"
                style={{
                  width: 16,
                  height: 1,
                  background: isCompleted ? 'var(--success, #10b981)' : 'var(--border, #e5e7eb)',
                  marginLeft: 4,
                  flexShrink: 0,
                }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}
